import { ActionAPI, AutocompleteAPI, FetchAPI, FieldAPI, FormAPI } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import { Inject } from '@odinjs/odin';
import Controller, { OnBeforeCancel, OnBeforeSubmit, OnChange, OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableSubmitEvent, ChangeEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpResponse, HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import moment from 'moment';
import { FragmentNames, PhysicianScheduleBlockDetailAliases } from '../../../codegen';
import { ISO_FULL_DATE_FORMAT } from '../../../constants/date-utils';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';

import { Endpoint } from '../../../constants/endpoints';
import { ApplicationRoute } from '../../../constants/routes';
import BaseController from '../../BaseController';
import { SimpleDialog } from '../../dialog-helper';
import DialogService from '../../../services/dialog-service';
import { DialogType, ModalType, SimpleDialogParams } from '../../../interfaces/simple-dialog';
import { DateFormatterUtil } from '@philips-emr/health-professional-components/src/utils';

interface ScheduleBlock {
  blockingReasonType: string;
  blockingReasonTypeDescription: string;
  createdUser: string;
  dayWeekDescription: string;
  dayWeekType: number;
  endBlockingHour: string;
  establishment: number;
  establishmentDescription: string;
  finalDate: string;
  initialDate: string;
  noteDescription: string;
  reasonScheduleBlockDescription: string;
  scheduleCode: number;
  scheduleDescription: string;
  sequenceNumber: number;
  startBlockingHour: string;
}

interface EstablishmentAutocompleteProps {
  establishmentCode: number;
}

@Controller({ component: PhysicianScheduleBlockDetailAliases.PHYSICIAN_SCHEDULE_BLOCK_DETAIL_LAYOUT, fragment: FragmentNames.PHYSICIAN_SCHEDULE_BLOCK_DETAIL })
class PhysicianScheduleBlockDetailController extends BaseController {

  @Inject(PhysicianScheduleBlockDetailAliases.PHYSICIAN_SCHEDULE_BLOCK_DETAIL_FORM)
    formScheduleApi!: FormAPI;

  @Inject(PhysicianScheduleBlockDetailAliases.PHYSICIAN_SCHEDULE_BLOCK_SCHEDULE_CODE_FETCH)
    scheduleFetchApi!: FetchAPI;

  @Inject(PhysicianScheduleBlockDetailAliases.PHYSICIAN_SCHEDULE_BLOCK_SCHEDULE_CODE_AUTOCOMPLETE)
    scheduleAutocompleteApi!: AutocompleteAPI;

  @Inject(PhysicianScheduleBlockDetailAliases.PHYSICIAN_SCHEDULE_BLOCK_ESTABLISHMENT_AUTOCOMPLETE)
    establishmentAutocompleteApi!: AutocompleteAPI;

  @Inject(PhysicianScheduleBlockDetailAliases.NEW_SCHEDULE_BLOCK_ACTION)
    newScheduleBlockActionApi!: ActionAPI;

  @Inject(PhysicianScheduleBlockDetailAliases.DELETE_SCHEDULE_BLOCK_ACTION)
    deleteScheduleBlockActionApi!: ActionAPI;

  @Inject(PhysicianScheduleBlockDetailAliases.PHYSICIAN_SCHEDULE_BLOCK_SCHEDULE_CODE_FIELD)
    scheduleFieldApi!: FieldAPI;

  goScheduleList(): void {
    applicationContext.redirect(ApplicationRoute.PHYSICIAN_SCHEDULE_BLOCK_FILTER);
  }

  @OnMount({ target: PhysicianScheduleBlockDetailAliases.PHYSICIAN_SCHEDULE_BLOCK_DETAIL_FORM })
  onMount(event: MountEvent): void {
    this.newScheduleBlockActionApi.setEnabled(false);
    this.deleteScheduleBlockActionApi.setEnabled(false);
    this.scheduleFieldApi.setVisible(false);
    const formApi = event.target as FormAPI;
    const sequenceNumber = this.getHistoryRouteParam<string>('sequenceNumber');

    if (sequenceNumber !== null) {
      this.context.set({ sequenceNumber });
      this.newScheduleBlockActionApi.setEnabled(true);
      this.deleteScheduleBlockActionApi.setEnabled(true);
      this.scheduleFieldApi.setVisible(true);
      this.getScheduleBlock(sequenceNumber).then(respSchedule => {
        const record = { ...formApi.getData(), ...respSchedule } as Record<string, Value>;
        formApi.setData(record);
      });
    }
  }

  @OnUnmount({ target: PhysicianScheduleBlockDetailAliases.PHYSICIAN_SCHEDULE_BLOCK_SCHEDULE_CODE_AUTOCOMPLETE })
  onMountSchedule(event: MountEvent): void {
    const scheduleApi = event.target as AutocompleteAPI;
    scheduleApi.setValue({} as Record<string, Value>);
  }

  @OnClick({ target: PhysicianScheduleBlockDetailAliases.NEW_SCHEDULE_BLOCK_ACTION })
  onClickNew(): void {
    applicationContext.redirect(ApplicationRoute.PHYSICIAN_SCHEDULE_BLOCK);
  }

  @OnBeforeSubmit({ target: PhysicianScheduleBlockDetailAliases.PHYSICIAN_SCHEDULE_BLOCK_DETAIL_FORM })
  onBeforeSubmitForm(event: AbortableSubmitEvent): void {
    event.abort();

    const sequence = this.context.get('sequenceNumber');
    const createdDate = sequence ? event.record.createdDate : null;
    const createdUser = sequence ? this.context.get('createdUser') : null;
    const dayWeekType = event.record.dayWeekType ? event.record.dayWeekType.dayWeekValue : null;
    const startHour = event.record.startBlockingHour ? DateFormatterUtil.timefieldToLocalTimezone(String(event.record.startBlockingHour)).split(':') : null;
    const endHour = event.record.endBlockingHour ? DateFormatterUtil.timefieldToLocalTimezone(String(event.record.endBlockingHour)).split(':') : null;
    const startDate = startHour ? moment(event.record.initialDate).hour(parseInt(startHour[0])).minute(parseInt(startHour[1])) : moment(event.record.initialDate);
    const endDate = endHour ? moment(event.record.finalDate).hour(parseInt(endHour[0])).minute(parseInt(endHour[1])) : moment(event.record.finalDate);
    const startDateFormatted = moment(event.record.initialDate).utc().format(ISO_FULL_DATE_FORMAT);
    const endDateFormatted = moment(event.record.finalDate).utc().format(ISO_FULL_DATE_FORMAT);
    const startBlockingHour = startHour ? moment(event.record.initialDate).hour(parseInt(startHour[0])).minute(parseInt(startHour[1])) : null;
    const endBlockingHour = endHour ? moment(event.record.finalDate).hour(parseInt(endHour[0])).minute(parseInt(endHour[1])) : null;

    if (startDate.isAfter(endDate)) {
      this.showMessageDialog('patient-evolution.error-message', 'tws.date-earlier.start-date', () => {});
      return;
    }

    const body = {
      blockingReasonType: event.record.blockingReasonType.domainValue,
      createdDate,
      createdUser,
      dayWeekType,
      endBlockingHour,
      establishment: event.record.establishment.establishmentCode,
      finalDate: endDateFormatted,
      initialDate: startDateFormatted,
      noteDescription: event.record.noteDescription,
      scheduleCode: event.record.scheduleCode.scheduleCode,
      sequenceNumber: sequence,
      startBlockingHour
    }

    HttpService.post(Endpoint.CREATE_PHYSICIAN_SCHEDULE_BLOCK, {}, { body })
      .then(() => {
        applicationContext.popNavigationItem();
      }).catch((error) => {
        return error.body.errors.map((message: string) => {
          SimpleDialog.showErrorMessage(message);
          applicationContext.popNavigationItem();
        });
      });
  }

  @OnBeforeCancel({ target: PhysicianScheduleBlockDetailAliases.PHYSICIAN_SCHEDULE_BLOCK_DETAIL_FORM })
  onFormBeforeCancel(): void {
    applicationContext.popNavigationItem();
  }

  @OnClick({ target: PhysicianScheduleBlockDetailAliases.DELETE_SCHEDULE_BLOCK_ACTION })
  onClickDeleteAction(): void {
    this.deleteScheduleBlockDialog();
  }

  @OnChange({ target: PhysicianScheduleBlockDetailAliases.PHYSICIAN_SCHEDULE_BLOCK_ESTABLISHMENT_AUTOCOMPLETE })
  onEstablishmentChange(event: ChangeEvent<EstablishmentAutocompleteProps>): void {
    const { establishmentCode } = event.value;
    const { sequenceNumber } = this.historyService.getCurrentRouteParams();

    if (establishmentCode) {
      sequenceNumber ? this.deleteScheduleBlockActionApi.setEnabled(true) : this.deleteScheduleBlockActionApi.setEnabled(false);
      this.newScheduleBlockActionApi.setEnabled(true);
      this.scheduleFieldApi.setVisible(true);
      this.context.set({ establishment: establishmentCode });
      return;
    }

    this.scheduleFieldApi.setVisible(false);
    this.deleteScheduleBlockActionApi.setEnabled(false);
    this.newScheduleBlockActionApi.setEnabled(false);
  }

  getScheduleBlock(sequenceNumber: number | string): Promise<Record<string, Value>> {
    return HttpService.get<ScheduleBlock>(Endpoint.GET_PHYSICIAN_SCHEDULE_BLOCK_DETAIL, { sequenceNumber })
      .then((response: HttpResponse<ScheduleBlock>) => {
        const responseBody = response.body as ScheduleBlock;
        const initialDateFormated = moment(String(responseBody.initialDate)).utc().format(ISO_DATE_FORMAT);
        const finalDateFormated = moment(String(responseBody.finalDate)).utc().format(ISO_DATE_FORMAT);
        const initialTimeFormated = responseBody.startBlockingHour ? moment(String(responseBody.startBlockingHour)).format(' LT') : '';
        const finalTimeFormated = responseBody.endBlockingHour ? moment(String(responseBody.endBlockingHour)).format(' LT') : '';

        const body = {
          blockingReasonType: {
            domainValue: responseBody.blockingReasonType,
            domainValueDescription: responseBody.blockingReasonTypeDescription
          },
          dayWeekType: {
            dayWeekDescription: responseBody.dayWeekDescription,
            dayWeekValue: responseBody.dayWeekType
          },
          endBlockingHour: finalTimeFormated,
          establishment: {
            establishmentCode: responseBody.establishment,
            establishmentDescription: responseBody.establishmentDescription
          },
          finalDate: finalDateFormated,
          initialDate: initialDateFormated,
          noteDescription: responseBody.noteDescription ? responseBody.noteDescription : '',
          scheduleCode: {
            scheduleCode: responseBody.scheduleCode,
            scheduleDescription: responseBody.scheduleDescription
          },
          startBlockingHour: initialTimeFormated
        };

        this.context.set({ createdUser: responseBody.createdUser });
        return body as Record<string, Value>;
      })
  }

  deleteScheduleBlock(sequenceNumber: number): void {
    const establishment = this.establishmentAutocompleteApi.getValue() as Record<string, Value>;
    const establishmentCode = establishment.establishmentCode;
    HttpService.delete(Endpoint.DELETE_PHYSICIAN_SCHEDULE_BLOCK, { establishmentCode, sequenceNumber }).then(() => {
      applicationContext.popNavigationItem();
    })
  }

  deleteScheduleBlockDialog(): void {
    const { sequenceNumber } = this.historyService.getCurrentRouteParams();

    DialogService.open({
      descriptionDialog: 'vita-sign.title-delete',
      onClickCancel: () => { return; },
      onClickOk: async () => { this.deleteScheduleBlock(Number(sequenceNumber)) },
      titleDialog: 'vita-sign.title-delete',
      titleOnCancel: 'sign-vital.btn-not-now',
      titleOnOk: 'vital-sign.btn-delete'
    })
  }

  showMessageDialog(title: string, description: string, onClickOk: () => void): void {

    const dialogParams: SimpleDialogParams = {
      closeable: false,
      descriptionDialog: description,
      dialogType: DialogType.ALERT,
      modalType: ModalType.DIALOG,
      onClickOk: onClickOk,
      titleDialog: title,
      titleOnOk: 'OK',
    }

    SimpleDialog.openDialog(dialogParams);
  }

}

export default PhysicianScheduleBlockDetailController;
